import React, { useState, useEffect, useRef } from "react";
import { m as motion, MAnimatePresence as AnimatePresence } from "../../shared/components/Motion";
import { useMotionValue } from "framer-motion";
import { THEME_CONFIG } from "config/themeConfig";
import "./glass-cursor.css";

const getContextInfo = (el) => {
  if (!el) return null;

  const tag = el.tagName?.toLowerCase();

  // Helper: read only direct text nodes, ignoring child element text (icons etc.)
  const directText = (node) =>
    Array.from(node.childNodes)
      .filter((n) => n.nodeType === Node.TEXT_NODE)
      .map((n) => n.textContent.trim())
      .join("")
      .trim();

  // Themes panel open/close button
  if (el.matches(".themes__button") || el.closest(".themes__button")) {
    // check if panel is currently open by looking for active-model
    const isOpen = !!document.querySelector(".themes__model.active-model");
    return { label: isOpen ? "Close Themes" : "Open Themes", sub: "panel" };
  }

  // Toggle switches (Dark Mode, Glass Effect, Color Mix, Custom Themes)
  if (
    el.matches(".toggle__switch-label") ||
    el.closest(".toggle__switch-label")
  ) {
    const label = el
      .closest(".themes__model-content, .colorpicker-toggle-container")
      ?.querySelector(".themes__model-title")
      ?.innerText?.trim();
    return label ? { label, sub: "toggle" } : null;
  }

  // Segmented control options (Soft / Balanced / Deep, intensity tabs)
  if (
    el.matches(".segmented-control-option") ||
    el.closest(".segmented-control-option")
  ) {
    const optEl = el.matches(".segmented-control-option")
      ? el
      : el.closest(".segmented-control-option");
    const text =
      optEl?.querySelector(".segmented-control-text")?.innerText?.trim() ||
      optEl?.innerText?.trim();
    return text ? { label: text, sub: "select" } : null;
  }

  // Theme color swatches (default & custom themes)
  if (
    el.matches(".themes_colors-button") ||
    el.closest(".themes_colors-button")
  ) {
    const btn = el.matches(".themes_colors-button")
      ? el
      : el.closest(".themes_colors-button");
    const name = btn?.getAttribute("title");
    if (name === "Clear Themes" || name === "Delete Selected Custom Theme") {
      return { label: name, sub: "action" };
    }
    return name
      ? { label: name, sub: "theme" }
      : { label: "Apply Theme", sub: "theme" };
  }

  // Glass texture buttons
  if (el.closest(".glass__texture-buttons")) {
    const btn = el.closest("button");
    const name = btn?.getAttribute("title");
    return name
      ? { label: name, sub: "texture" }
      : { label: "Set Texture", sub: "texture" };
  }

  // Range sliders (Blur / Opacity / Saturation / Color Mix)
  if (
    el.matches("input[type='range']") ||
    el.closest(".color-mix__container")
  ) {
    const container = el.closest(".color-mix__container");
    const labelText = container
      ?.querySelector(".glass_label, .color-mix-label")
      ?.innerText?.trim();
    return labelText
      ? { label: `Adjust ${labelText}`, sub: "slider" }
      : { label: "Adjust", sub: "slider" };
  }

  // Add new theme / save custom theme button
  if (
    el.matches(".AddNewTheme__buttonComponent") ||
    el.closest(".AddNewTheme__buttonComponent")
  ) {
    const text =
      el.closest(".AddNewTheme__buttonComponent")?.innerText?.trim() ||
      "Save Theme";
    return { label: text, sub: "action" };
  }

  // Color property inputs in custom theme form
  if (
    el.matches(".color-property__input, input[type='color']") ||
    el.closest(".color-property")
  ) {
    const propLabel = el
      .closest(".color-property")
      ?.querySelector(".color-property__label, label")
      ?.innerText?.trim();
    return propLabel
      ? { label: propLabel, sub: "color" }
      : { label: "Pick Color", sub: "color" };
  }

  // Nav links
  if (el.matches(".nav__link, .nav__logo")) {
    const text = directText(el) || el.innerText?.trim();
    return text ? { label: text, sub: "navigate" } : null;
  }

  // Work cards
  if (el.matches(".work__card") || el.closest(".work__card") === el) {
    const title = el
      .querySelector("[class*='title'], h3, h4")
      ?.innerText?.trim();
    return { label: title || "Open", sub: "project" };
  }

  // Work filter buttons (All / Frontend / Backend...)
  if (el.matches(".work__item")) {
    const text = directText(el) || el.innerText?.trim();
    return text ? { label: text, sub: "filter" } : null;
  }

  // Work "View Project" button
  if (
    el.matches(".work__button, .work__button-container") ||
    el.closest(".work__button-container")
  ) {
    return { label: "View Project", sub: "action" };
  }

  // Skills filter tabs
  if (el.matches(".skills__filter-btn, .skills__filter-item")) {
    const text = directText(el) || el.innerText?.trim();
    return text ? { label: text, sub: "filter" } : null;
  }

  // Skill chips
  if (el.matches(".skills__chip")) {
    const text = directText(el) || el.innerText?.trim();
    return text ? { label: text, sub: "skill" } : null;
  }

  // Certificate cards
  if (
    el.matches(".certificates__content") ||
    el.closest(".certificates__content")
  ) {
    const title =
      el.querySelector(".certificates__title, h3")?.innerText?.trim() ||
      el
        .closest(".certificates__content")
        ?.querySelector(".certificates__title, h3")
        ?.innerText?.trim();
    return title
      ? { label: title, sub: "certificate" }
      : { label: "View Certificate", sub: "certificate" };
  }

  // Certificate button
  if (
    el.matches(".certificates__button") ||
    el.closest(".certificates__button")
  ) {
    return { label: "View Certificate", sub: "open" };
  }

  // Qualification tabs (Education / Work)
  if (
    el.matches(".qualification__button") ||
    el.closest(".qualification__button")
  ) {
    const text = directText(el) || el.innerText?.trim();
    return text ? { label: text, sub: "experience" } : null;
  }

  // Contact cards
  if (el.matches(".contact__card") || el.closest(".contact__card")) {
    const title =
      el.querySelector(".contact__card-title")?.innerText?.trim() ||
      el
        .closest(".contact__card")
        ?.querySelector(".contact__card-title")
        ?.innerText?.trim();
    const data =
      el.querySelector(".contact__card-data")?.innerText?.trim() ||
      el
        .closest(".contact__card")
        ?.querySelector(".contact__card-data")
        ?.innerText?.trim();
    return title ? { label: title, sub: data || "contact" } : null;
  }

  // Contact send button
  if (el.matches(".contact__button") || el.closest(".contact__button")) {
    return { label: "Send Message", sub: "action" };
  }

  // Home scroll-down
  if (el.matches(".home__scroll") || el.closest(".home__scroll")) {
    return { label: "Scroll Down", sub: "explore" };
  }

  // Home social icons
  if (el.matches(".home__social-icon") || el.closest(".home__social-icon")) {
    const href = el.closest("a")?.href || "";
    if (href.includes("github")) return { label: "GitHub", sub: "profile" };
    if (href.includes("linkedin")) return { label: "LinkedIn", sub: "profile" };
    if (href.includes("twitter") || href.includes("x.com"))
      return { label: "Twitter", sub: "profile" };
    if (href.includes("instagram"))
      return { label: "Instagram", sub: "profile" };
    return { label: "Social", sub: "profile" };
  }

  // Footer nav links
  if (el.matches(".footer__link") || el.closest(".footer__link")) {
    const text = directText(el) || el.innerText?.trim();
    return text ? { label: text, sub: "navigate" } : null;
  }

  // Footer social links
  if (el.closest(".footer__social a, .footer__social-link")) {
    const href = el.closest("a")?.href || "";
    if (href.includes("github")) return { label: "GitHub", sub: "profile" };
    if (href.includes("linkedin")) return { label: "LinkedIn", sub: "profile" };
    if (href.includes("twitter") || href.includes("x.com"))
      return { label: "Twitter", sub: "profile" };
    if (href.includes("instagram"))
      return { label: "Instagram", sub: "profile" };
    return { label: "Social", sub: "profile" };
  }

  // Scroll-up button
  if (
    el.matches(".scrollup, .scroll-up") ||
    el.closest(".scrollup, .scroll-up")
  ) {
    return { label: "Back to Top", sub: "scroll" };
  }

  // Preview modal links (View on site / View Code / Live Demo)
  if (el.matches(".preview-link") || el.closest(".preview-link")) {
    const text = directText(el) || el.innerText?.trim();
    return text
      ? { label: text, sub: "open" }
      : { label: "Open Link", sub: "open" };
  }

  // About info items
  if (el.matches(".about__info-item") || el.closest(".about__info-item")) {
    const title =
      el
        .querySelector(".about__info-title, .about__info-name")
        ?.innerText?.trim() ||
      el
        .closest(".about__info-item")
        ?.querySelector(".about__info-title, .about__info-name")
        ?.innerText?.trim();
    return title ? { label: title, sub: "info" } : null;
  }

  // Home buttons (Download CV / Contact Me)
  if (
    el.matches(".home__button, .button, .button--flex") ||
    el.closest(".home__button, .home__scroll-button")
  ) {
    const text = directText(el) || el.innerText?.trim();
    return text ? { label: text, sub: "action" } : null;
  }

  // Generic inputs
  if (tag === "input" || tag === "textarea") {
    const labelEl = el
      .closest(".contact__form-div, .contact__form-field")
      ?.querySelector("label");
    const label =
      labelEl?.innerText?.trim() ||
      el.getAttribute("placeholder") ||
      "Type here";
    return { label, sub: "input" };
  }

  // Generic buttons (aria-label only)
  if (tag === "button") {
    const raw = el.getAttribute("aria-label");
    if (raw && raw.trim().length > 0) {
      return { label: raw.trim(), sub: "action" };
    }
    return null;
  }

  // Generic anchor tags (aria-label or title only)
  if (tag === "a") {
    const raw = el.getAttribute("aria-label") || el.getAttribute("title");
    if (raw && raw.trim().length > 1) {
      return { label: raw.trim(), sub: "link" };
    }
    return null;
  }

  return null;
};

const getInteractiveElement = (target) => {
  if (!target) return null;
  return (
    target.closest(".themes__button") ||
    target.closest(".segmented-control-option") ||
    target.closest(".themes_colors-button") ||
    target.closest(".glass__texture-buttons button") ||
    target.closest(".color-mix__container") ||
    target.closest(".AddNewTheme__buttonComponent") ||
    target.closest(".color-property__input, input[type='color']") ||
    target.closest(".color-property") ||
    target.closest(".nav__link, .nav__logo") ||
    target.closest(".work__card") ||
    target.closest(".work__item") ||
    target.closest(".work__button-container") ||
    target.closest(".skills__chip") ||
    target.closest(".skills__filter-btn, .skills__filter-item") ||
    target.closest(".certificates__content") ||
    target.closest(".certificates__button") ||
    target.closest(".qualification__button") ||
    target.closest(".contact__card") ||
    target.closest(".contact__button") ||
    target.closest(".home__scroll") ||
    target.closest(".home__social-icon") ||
    target.closest(".footer__link") ||
    target.closest(".footer__social a, .footer__social-link") ||
    target.closest(".scrollup, .scroll-up") ||
    target.closest(".preview-link") ||
    target.closest(".about__info-item") ||
    target.closest(".home__button, .home__scroll-button") ||
    target.closest("input, textarea") ||
    target.closest("button") ||
    target.closest("a") ||
    null
  );
};

// ── Cursor icons (20×20 SVG, stroke-based) ───────────────────────────────────
const S = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.75,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};
const ICONS = {
  pointer: (
    <svg viewBox="0 0 20 20" {...S}>
      <path
        d="M5 2 l0 12 3-3 2 5.5 2.3-.9-2-5.5H14Z"
        fill="currentColor"
        stroke="none"
      />
    </svg>
  ),
  eye: (
    <svg viewBox="0 0 20 20" {...S}>
      <path d="M2 10s3.5-6 8-6 8 6 8 6-3.5 6-8 6-8-6-8-6z" />
      <circle cx="10" cy="10" r="2.5" fill="currentColor" stroke="none" />
    </svg>
  ),
  text: (
    <svg viewBox="0 0 20 20" {...S}>
      <line x1="10" y1="4" x2="10" y2="16" />
      <line x1="7" y1="4" x2="13" y2="4" />
      <line x1="7" y1="16" x2="13" y2="16" />
    </svg>
  ),
  external: (
    <svg viewBox="0 0 20 20" {...S}>
      <path d="M11 4h5v5" />
      <line x1="16" y1="4" x2="9" y2="11" />
      <path d="M8 5H5a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-3" />
    </svg>
  ),
  message: (
    <svg viewBox="0 0 20 20" {...S}>
      <path d="M3 5h14a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7l-4 3V6a1 1 0 0 1 1-1z" />
    </svg>
  ),
  palette: (
    <svg viewBox="0 0 20 20" {...S}>
      <path d="M10 2a8 8 0 1 0 5.66 13.66C17.08 14.24 16 12 16 12h-3a2 2 0 0 1-2-2V8a2 2 0 0 0-2-2z" />
      <circle cx="6.5" cy="7" r="1" fill="currentColor" stroke="none" />
      <circle cx="6.5" cy="13" r="1" fill="currentColor" stroke="none" />
      <circle cx="10" cy="5.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  ),
  gear: (
    <svg viewBox="0 0 20 20" {...S}>
      <circle cx="10" cy="10" r="2.5" />
      <path d="M10 1v3M10 16v3M1 10h3M16 10h3M3.64 3.64l2.12 2.12M14.24 14.24l2.12 2.12M3.64 16.36l2.12-2.12M14.24 5.76l2.12-2.12" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 20 20" {...S}>
      <circle cx="10" cy="10" r="7.5" />
      <path d="M6.5 10 L9 12.5 L13.5 7.5" />
    </svg>
  ),
  brush: (
    <svg viewBox="0 0 20 20" {...S}>
      <path
        d="M15 2 L18 5 L9.5 13.5 L6.5 10.5 Z"
        fill="currentColor"
        stroke="none"
      />
      <path d="M9.5 13.5 C7 16 3 17 3 17 C3 17 4 13 6.5 10.5" />
    </svg>
  ),
  filter: (
    <svg viewBox="0 0 20 20" {...S}>
      <path d="M3 5h14l-5.5 7v5l-3-1.5V12Z" />
    </svg>
  ),
  code: (
    <svg viewBox="0 0 20 20" {...S}>
      <path d="M7 6.5 L3 10 L7 13.5" />
      <path d="M13 6.5 L17 10 L13 13.5" />
      <line x1="11" y1="5" x2="9" y2="15" />
    </svg>
  ),
  award: (
    <svg viewBox="0 0 20 20" {...S}>
      <circle cx="10" cy="8" r="5" />
      <path d="M7 12.5 L5.5 18 L10 16 L14.5 18 L13 12.5" />
    </svg>
  ),
  brief: (
    <svg viewBox="0 0 20 20" {...S}>
      <rect x="2" y="8" width="16" height="10" rx="1.5" />
      <path d="M7 8V6.5A1.5 1.5 0 0 1 8.5 5h3A1.5 1.5 0 0 1 13 6.5V8" />
      <line x1="2" y1="12" x2="18" y2="12" />
    </svg>
  ),
  info: (
    <svg viewBox="0 0 20 20" {...S}>
      <circle cx="10" cy="10" r="8" />
      <line x1="10" y1="9" x2="10" y2="14" />
      <circle cx="10" cy="6.5" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  ),
  "scroll-up": (
    <svg viewBox="0 0 20 20" {...S}>
      <line x1="10" y1="4" x2="10" y2="15" />
      <path d="M5 9 L10 4 L15 9" />
      <line x1="5" y1="17" x2="15" y2="17" />
    </svg>
  ),
  "scroll-down": (
    <svg viewBox="0 0 20 20" {...S}>
      <line x1="10" y1="5" x2="10" y2="16" />
      <path d="M5 11 L10 16 L15 11" />
    </svg>
  ),
  adjust: (
    <svg viewBox="0 0 20 20" {...S}>
      <line x1="3" y1="7" x2="17" y2="7" />
      <line x1="3" y1="13" x2="17" y2="13" />
      <circle cx="8" cy="7" r="2.5" fill="currentColor" stroke="none" />
      <circle cx="12" cy="13" r="2.5" fill="currentColor" stroke="none" />
    </svg>
  ),
  dropper: (
    <svg viewBox="0 0 20 20" {...S}>
      <path d="M14 2 L18 6 L9 15 L5 14 L4 10 Z" />
      <line x1="11" y1="5" x2="15" y2="9" />
      <circle cx="3" cy="17" r="1.5" fill="currentColor" stroke="none" />
      <path d="M4 10 L3 17" strokeDasharray="1.8 1.5" />
    </svg>
  ),
  bolt: (
    <svg viewBox="0 0 20 20" fill="currentColor">
      <path d="M11.5 2 L5 11.5 H9.5 L7.5 18 L17 8 H12 Z" />
    </svg>
  ),
  navigate: (
    <svg viewBox="0 0 20 20" fill="currentColor">
      <path d="M4.5 2 L4.5 13.5 L8.5 9.5 L10.5 15 L13 14 L11 8.5 L16 8.5 Z" />
    </svg>
  ),
  link: (
    <svg viewBox="0 0 20 20" {...S}>
      <path d="M8 12.5 A3.5 3.5 0 0 1 8 5.5 H11 A3.5 3.5 0 0 1 11 12.5 H9.5" />
      <path d="M12 7.5 A3.5 3.5 0 0 1 12 14.5 H9 A3.5 3.5 0 0 1 9 7.5 H10.5" />
    </svg>
  ),
};

// ── Map context sub → cursor type → dimensions ──────────────────────────────
const SUB_TO_TYPE = {
  navigate: "navigate",
  project: "eye",
  filter: "filter",
  profile: "link",
  skill: "code",
  certificate: "award",
  experience: "brief",
  contact: "message",
  action: "bolt",
  input: "text",
  panel: "gear",
  toggle: "check",
  select: "check",
  theme: "palette",
  texture: "brush",
  slider: "adjust",
  color: "dropper",
  scroll: "scroll-up",
  explore: "scroll-down",
  open: "external",
  link: "link",
  info: "info",
};

const getCursorType = (ctx) => (ctx ? SUB_TO_TYPE[ctx.sub] || "pointer" : null);

const getCursorDims = (type, isActive) => {
  if (!isActive) return { w: 32, h: 32, r: "50%" };
  if (type === "text") return { w: 18, h: 42, r: "5px" };
  if (type === "scroll-up" || type === "scroll-down")
    return { w: 28, h: 44, r: "14px" };
  return { w: 44, h: 44, r: "50%" };
};

const isTextInput = (el) =>
  el &&
  ((el.tagName === "INPUT" &&
    ![
      "checkbox",
      "radio",
      "color",
      "range",
      "submit",
      "button",
      "file",
    ].includes(el.type)) ||
    el.tagName === "TEXTAREA");

const GlassCursor = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [context, setContext] = useState(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [stretch, setStretch] = useState({ x: 1, y: 1 });

  const scrollTimeoutRef = useRef(null);
  const lastElementRef = useRef(null);
  const contextDebounceRef = useRef(null);
  const pendingContextRef = useRef(null);
  const cursorRef = useRef(null);
  const prevPosRef = useRef({ x: 0, y: 0, t: 0 });
  const stretchResetRef = useRef(null);

  const mouseX = useMotionValue(-300);
  const mouseY = useMotionValue(-300);

  // ── Input focus: hide cursor when user is actively typing ─────────────────
  useEffect(() => {
    const onIn = (e) => {
      if (isTextInput(e.target)) setIsFocused(true);
    };
    const onOut = (e) => {
      if (isTextInput(e.target)) setIsFocused(false);
    };
    document.addEventListener("focusin", onIn);
    document.addEventListener("focusout", onOut);
    return () => {
      document.removeEventListener("focusin", onIn);
      document.removeEventListener("focusout", onOut);
    };
  }, []);

  // ── Visibility (desktop + enabled toggle, same as before) ─────────────────
  useEffect(() => {
    const raw = localStorage.getItem("glassCursorEnabled");
    const glassCursorEnabled =
      raw !== null ? JSON.parse(raw) : THEME_CONFIG.glassCursorEnabled;
    const isDesktop = window.innerWidth > 968;
    const shouldShow = isDesktop && glassCursorEnabled;
    setIsVisible(shouldShow);
    document.body.classList.toggle("glass-cursor-active", shouldShow);

    const handleResize = () => {
      const desktop = window.innerWidth > 968;
      const resRaw = localStorage.getItem("glassCursorEnabled");
      const enabled =
        resRaw !== null ? JSON.parse(resRaw) : THEME_CONFIG.glassCursorEnabled;
      const show = desktop && enabled;
      setIsVisible(show);
      document.body.classList.toggle("glass-cursor-active", show);
    };
    const handleGlassCursorToggle = (e) => {
      const show = window.innerWidth > 968 && e.detail.enabled;
      setIsVisible(show);
      document.body.classList.toggle("glass-cursor-active", show);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("glassCursorToggle", handleGlassCursorToggle);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("glassCursorToggle", handleGlassCursorToggle);
      document.body.classList.remove("glass-cursor-active");
    };
  }, []);

  // ── Mousemove: position + velocity stretch + tail + context ───────────────
  useEffect(() => {
    if (!isVisible) return;

    const handleMouseMove = (e) => {
      const margin = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let x = Math.max(margin, Math.min(vw - margin, e.clientX));
      let y = Math.max(margin, Math.min(vh - margin, e.clientY));

      // Velocity
      const now = performance.now();
      const dt = Math.max(now - prevPosRef.current.t, 1);
      const dx = x - prevPosRef.current.x;
      const dy = y - prevPosRef.current.y;
      const speed = Math.sqrt(dx * dx + dy * dy) / dt; // px/ms
      prevPosRef.current = { x, y, t: now };

      mouseX.set(x);
      mouseY.set(y);

      // Directional stretch (elastic squash-and-stretch)
      if (speed > 0.45) {
        const angle = Math.atan2(dy, dx);
        const amt = Math.min(speed * 0.9, 0.18);
        setStretch({
          x: 1 + Math.abs(Math.cos(angle)) * amt,
          y: 1 + Math.abs(Math.sin(angle)) * amt,
        });
        clearTimeout(stretchResetRef.current);
        stretchResetRef.current = setTimeout(
          () => setStretch({ x: 1, y: 1 }),
          90,
        );
      }

      // Context detection (80ms debounce — kills flicker on fast traversal)
      const interactiveEl = getInteractiveElement(e.target);
      if (interactiveEl !== lastElementRef.current) {
        lastElementRef.current = interactiveEl;
        const newContext = interactiveEl ? getContextInfo(interactiveEl) : null;
        pendingContextRef.current = newContext;
        clearTimeout(contextDebounceRef.current);
        contextDebounceRef.current = setTimeout(
          () => setContext(pendingContextRef.current),
          80,
        );
      }
    };

    const handleScroll = () => {
      setIsScrolling(true);
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => setIsScrolling(false), 150);
    };

    document.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("scroll", handleScroll);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimeoutRef.current);
      clearTimeout(stretchResetRef.current);
      clearTimeout(contextDebounceRef.current);
    };
  }, [isVisible, mouseX, mouseY]);

  if (!isVisible) return null;

  const cursorType = getCursorType(context);
  const isActive = !!context;
  const dims = getCursorDims(cursorType, isActive);

  return (
    <>
      {/* ── Main glass cursor ──────────────────────────────────────────── */}
      <motion.div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 0,
          height: 0,
          zIndex: 99999,
          pointerEvents: "none",
          willChange: "transform",
          x: mouseX,
          y: mouseY,
        }}
        animate={{
          opacity: isFocused ? 0 : 1,
          scale: isFocused ? 0.6 : 1,
          scaleX: isFocused ? 0.6 : stretch.x,
          scaleY: isFocused ? 0.6 : stretch.y,
        }}
        transition={{ type: "spring", damping: 12, stiffness: 480, mass: 0.15 }}
      >
        <div className="glass-cursor__anchor">
          <motion.div
            ref={cursorRef}
            className={`glass-cursor${isScrolling ? " glass-cursor--scrolling" : ""}${isActive ? " glass-cursor--active" : ""}`}
            animate={{
              width: dims.w,
              height: dims.h,
              borderRadius: dims.r,
              opacity: isScrolling ? 0.35 : 1,
            }}
            transition={{
              width: {
                type: "tween",
                duration: 0.22,
                ease: [0.22, 1, 0.36, 1],
              },
              height: {
                type: "tween",
                duration: 0.22,
                ease: [0.22, 1, 0.36, 1],
              },
              borderRadius: {
                type: "spring",
                damping: 22,
                stiffness: 280,
                mass: 0.5,
              },
              opacity: {
                type: "spring",
                damping: 22,
                stiffness: 280,
                mass: 0.5,
              },
            }}
          >
            {/* Glass layers */}
            <div className="glass-cursor__overlay" />
            <div className="glass-cursor__specular" />

            {/* Icon */}
            <div className="glass-cursor__content">
              <AnimatePresence mode="wait">
                {isActive ? (
                  <motion.div
                    key={cursorType}
                    className="glass-cursor__icon"
                    initial={{ opacity: 0, scale: 0.5, rotate: -15 }}
                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                    exit={{ opacity: 0, scale: 0.5, rotate: 15 }}
                    transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {ICONS[cursorType] ?? ICONS.pointer}
                  </motion.div>
                ) : (
                  <motion.div
                    key="idle"
                    className="glass-cursor__idle-glow"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  />
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </>
  );
};

export default GlassCursor;
